import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Text,
  VStack,
  SimpleGrid,
  HStack,
  Badge,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Link,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Spinner,
  Image,
  IconButton,
  ModalFooter,
  Select,
  Textarea,
  Input,
} from '@chakra-ui/react'
import { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { RequestOption } from '@/types/ticket'
import type { FieldEngineerRecommendation } from '@/types/ticket'
import type { FieldEngineerSummary } from '@/store/parentTicket'
import type { FESchedule } from '@/types/parentTicket'
import { TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import PartsInfoSection from './PartsInfoSection'

type Asset = {
  id: number
  tag_number?: string | null
  model?: string | null
  serial_number?: string | null
  brand_name?: string | null
  category_name?: string | null
  function?: string | null
  [key: string]: any
}

type TicketData = {
  ticketNumber?: string | null
  description?: string | null
  notes?: string | null
  workPermit?: string | null
  workPermitPath?: string | null
  scheduledAt?: string | null
  assets?: Asset[]
  subject?: string | null
  type?: string | null
  source?: string | null
  priority?: string | null
  severity?: string | null
  supportType?: string | null
  status?: string | null
}

type Props = {
  /** Full request object from the API (ticket_requests table) */
  request?: RequestOption | null
  /** Parent ticket data (parent_tickets table) */
  ticket?: TicketData | null
  /** Ticket ID for loading FE schedules and parts (when in View page) */
  ticketId?: string | null
  /** Callback when work permit file is uploaded (View page) */
  onWorkPermitUpload?: (ticketId: string, file: File) => Promise<void>
  /** Store ID for Recommend FE (View page) */
  storeId?: number | null
  /** Store/Client code for Recommend FE API (View page) - use client code e.g. JFC, not store branch code */
  storeCode?: string | null
  /** Client code for Recommend FE API (X-Client-Code) - from ticket request; use this for recommend, not storeCode */
  clientCode?: string | null
  /** Callback after FE schedules change (refresh activity log) */
  onSchedulesChange?: () => void | Promise<void>
  /** Callback after parts change (refresh activity log) */
  onPartsChange?: () => void
  /** Callback after notes are updated (refresh ticket in View) */
  onNotesUpdate?: () => void
  /** True while ticket is refetching after FE schedule add/edit/delete (show preloader in FE Schedule block) */
  feScheduleRefreshing?: boolean
  /** Primary engineer (same as Assigned Team card) */
  primaryEngineer?: FieldEngineerSummary | null
  /** Backup engineer (same as Assigned Team card) */
  backupEngineer?: FieldEngineerSummary | null
  /** Store details (shown inside Ticket Information) */
  storeDetails?: {
    businessUnitName?: string | null
    storeName?: string | null
    branchCode?: string | null
    contactPerson?: string | null
    email?: string | null
    contactNumber?: string | null
    storeAddress?: string | null
  } | null

  isReadOnly?: boolean
  /** Omit outer card chrome; tighter grids (e.g. parent ticket view single card) */
  embedded?: boolean
}

function Pair({ label, value }: { label: string; value?: string | null }) {
  const colors = useThemeColors()
  return (
    <Box>
      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" color={colors.text} fontWeight="500">
        {value || '—'}
      </Text>
    </Box>
  )
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/** Format ISO date string in UTC so display matches DB value (e.g. 2026-03-11 23:00:00 → Mar 11, 2026, 11:00 PM) */
function formatDateUTC(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const SEVERITY_LABELS: Record<string, string> = {
  '1': 'Severity 1',
  '2': 'Severity 2',
  '3': 'Severity 3',
  '4': 'Severity 4',
  '5': 'Severity 5',
}

function SectionHeader({ children, mb }: { children: React.ReactNode; mb?: number }) {
  const colors = useThemeColors()
  return (
    <Text fontSize="xs" fontWeight="700" color={colors.textSecondary} textTransform="uppercase" letterSpacing="wider" mb={mb}>
      {children}
    </Text>
  )
}

const SUPPORT_TYPE_LABELS: Record<string, string> = {
  l1_basic: 'L1 - Basic troubleshooting',
  l1_remote: 'L1 - 1.5 Remote support',
  l2_network: 'L2 - Network/Advanced support',
  l3_field: 'L3 - Client Support/Field Support',
}

const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api'

function WorkPermitThumbnail({ ticketId, path }: { ticketId: string; path: string }) {
  const colors = useThemeColors()
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)
  const blobUrlRef = useRef<string | null>(null)
  const apiUrl = `${apiBaseUrl}/tickets/${ticketId}/work-permit`
  const isImage = /\.(jpe?g|png|gif|webp)$/i.test(path)

  useEffect(() => {
    if (!ticketId || !path) return
    setLoadError(false)
    apiClient
      .get(apiUrl, { responseType: 'blob' })
      .then((res) => {
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
        const url = URL.createObjectURL(res.data)
        blobUrlRef.current = url
        setBlobUrl(url)
      })
      .catch(() => setLoadError(true))

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
      setBlobUrl(null)
    }
  }, [ticketId, path, apiUrl])

  const openInNewTab = (e: React.MouseEvent) => {
    e.preventDefault()
    if (blobUrl) {
      window.open(blobUrl, '_blank')
    } else {
      apiClient.get(apiUrl, { responseType: 'blob' }).then((res) => {
        const u = URL.createObjectURL(res.data)
        window.open(u, '_blank')
      })
    }
  }

  if (loadError) {
    return (
      <Text fontSize="sm" color="red.500">
        Failed to load file
      </Text>
    )
  }

  if (isImage) {
    return (
      <Box
        as="a"
        href={blobUrl ?? '#'}
        onClick={blobUrl ? openInNewTab : undefined}
        flexShrink={0}
        borderRadius="md"
        overflow="hidden"
        border="1px solid"
        borderColor={colors.borderLight}
        _hover={{ opacity: 0.9 }}
        cursor={blobUrl ? 'pointer' : 'wait'}
      >
        {blobUrl ? (
          <Image src={blobUrl} alt="Work permit" maxW="120px" maxH="120px" objectFit="cover" />
        ) : (
          <Box w="120px" h="120px" bg={colors.borderLight} display="flex" alignItems="center" justifyContent="center">
            <Spinner size="sm" />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Link
      href="#"
      onClick={openInNewTab}
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="120px"
      h="80px"
      borderRadius="md"
      border="1px solid"
      borderColor={colors.borderLight}
      bg={colors.inputBg ?? 'gray.50'}
      fontSize="xs"
      fontWeight="600"
      color={colors.textSecondary}
      _hover={{ bg: colors.borderLight }}
    >
      PDF / Document
    </Link>
  )
}

export default function TicketProperties({
  request,
  ticket,
  ticketId,
  onWorkPermitUpload,
  storeId,
  storeCode,
  clientCode,
  onSchedulesChange,
  onPartsChange,
  onNotesUpdate,
  feScheduleRefreshing,
  primaryEngineer,
  backupEngineer,
  storeDetails,
  isReadOnly,
  embedded = false,
}: Props) {
  const colors = useThemeColors()
  const gridX = embedded ? 6 : 8
  const gridY = embedded ? 2 : 3
  const sectionVSpacing = embedded ? 3 : 4
  const mainVSpacing = embedded ? 3 : 5
  const { showSuccess, showError } = useToastNotification()
  const { isOpen: isRecommendOpen, onOpen: onRecommendOpen, onClose: onRecommendClose } = useDisclosure()
  const [recommendations, setRecommendations] = useState<FieldEngineerRecommendation[]>([])
  const [recommendLoading, setRecommendLoading] = useState(false)
  /** Selected date for daily timeline (default today) */
  const [recommendSelectedDate, setRecommendSelectedDate] = useState<Date>(() => new Date())
  /** Per-FE schedules for the selected date (cross-ticket), keyed by fe_user_id */
  const [feAvailabilityByUser, setFeAvailabilityByUser] = useState<Record<number, FESchedule[]>>({})
  const [recommendScheduleLoading, setRecommendScheduleLoading] = useState(false)
  const { isOpen: isDeleteScheduleOpen, onOpen: onDeleteScheduleOpen, onClose: onDeleteScheduleClose } = useDisclosure()
  const [scheduleToDelete, setScheduleToDelete] = useState<number | null>(null)
  const cancelDeleteRef = useRef<HTMLButtonElement>(null)
  const { isOpen: isAssignModalOpen, onOpen: onAssignModalOpen, onClose: onAssignModalClose } = useDisclosure()
  const [assignModalRec, setAssignModalRec] = useState<FieldEngineerRecommendation | null>(null)
  const [assignModalDateStr, setAssignModalDateStr] = useState('')
  const [assignModalTime, setAssignModalTime] = useState('09:00')
  const [assignModalEndTime, setAssignModalEndTime] = useState('10:00')
  const [assignModalRole, setAssignModalRole] = useState<'primary' | 'secondary'>('primary')
  const [assignSubmitting, setAssignSubmitting] = useState(false)
  const [editingScheduleId, setEditingScheduleId] = useState<number | null>(null)
  const [editScheduleTime, setEditScheduleTime] = useState('09:00')
  const [editScheduleEndTime, setEditScheduleEndTime] = useState('10:00')
  const [editScheduleRole, setEditScheduleRole] = useState<'primary' | 'secondary'>('primary')
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [deleteSubmittingId, setDeleteSubmittingId] = useState<number | null>(null)
  const [schedulesRefreshKey, setSchedulesRefreshKey] = useState(0)
  const { isOpen: isNotesModalOpen, onOpen: onNotesModalOpen, onClose: onNotesModalClose } = useDisclosure()
  const [notesDraft, setNotesDraft] = useState('')
  const [notesSaving, setNotesSaving] = useState(false)

  const refreshSchedulesInView = useCallback(() => {
    setSchedulesRefreshKey((k) => k + 1)
    onSchedulesChange?.()
  }, [onSchedulesChange])

  /** Selected date as Y-m-d for API */
  const recommendDateStr = useMemo(
    () =>
      `${recommendSelectedDate.getFullYear()}-${String(recommendSelectedDate.getMonth() + 1).padStart(2, '0')}-${String(recommendSelectedDate.getDate()).padStart(2, '0')}`,
    [recommendSelectedDate]
  )

  const fetchFEAvailabilityForDate = useCallback(
    async (dateStr: string) => {
      if (!ticketId || recommendations.length === 0) return
      setRecommendScheduleLoading(true)
      const byUser: Record<number, FESchedule[]> = {}
      await Promise.all(
        recommendations.map(async (rec) => {
          const feUserId = rec.user_id ?? rec.field_engineer_id
          const res = await TicketService.getFEAvailability(ticketId, feUserId, dateStr)
          if (res.success && res.data) byUser[feUserId] = res.data
          else byUser[feUserId] = []
        })
      )
      setFeAvailabilityByUser(byUser)
      setRecommendScheduleLoading(false)
    },
    [ticketId, recommendations]
  )

  useEffect(() => {
    if (isRecommendOpen && ticketId && recommendations.length > 0) {
      fetchFEAvailabilityForDate(recommendDateStr)
    }
  }, [isRecommendOpen, ticketId, recommendations, recommendDateStr, fetchFEAvailabilityForDate])

  const handleRecommendFE = useCallback(async () => {
    if (storeId == null) return
    setRecommendLoading(true)
    setRecommendations([])
    onRecommendOpen()
    const res = await TicketService.recommendFieldEngineers(Number(storeId), clientCode ?? storeCode ?? undefined)
    setRecommendLoading(false)
    if (res.success && res.data) setRecommendations(res.data)
  }, [storeId, clientCode, storeCode, onRecommendOpen])

  /** Hour slots for daily timeline (24-hour, 00:00–23:00) */
  const timelineHours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), [])
  const today = new Date()
  const isSelectedToday =
    recommendSelectedDate.getFullYear() === today.getFullYear() &&
    recommendSelectedDate.getMonth() === today.getMonth() &&
    recommendSelectedDate.getDate() === today.getDate()

  const getFeUserId = (rec: FieldEngineerRecommendation) => rec.user_id ?? rec.field_engineer_id

  /** Parse "H:i" to minutes since midnight; returns 0 for invalid/empty */
  const timeToMinutes = useCallback((time: string | null | undefined): number => {
    if (!time || typeof time !== 'string') return 0
    const parts = time.trim().slice(0, 5).split(':')
    const h = parseInt(parts[0] ?? '0', 10)
    const m = parseInt(parts[1] ?? '0', 10)
    return h * 60 + m
  }, [])

  /** Schedules for an FE that cover the given hour (range [hour:00, hour+1:00) intersects schedule range) */
  const getScheduleForHour = useCallback(
    (feUserId: number, hour: number): FESchedule[] => {
      const list = feAvailabilityByUser[feUserId] ?? []
      const hourStart = hour * 60
      const hourEnd = hourStart + 60
      return list.filter((s) => {
        const startM = timeToMinutes(s.schedule_time)
        const endM = s.schedule_end_time ? timeToMinutes(s.schedule_end_time) : startM + 60
        return startM < hourEnd && endM > hourStart
      })
    },
    [feAvailabilityByUser, timeToMinutes]
  )

  /** Merged timeline blocks for an FE on the selected date: each block spans [startHour, endHour) with one schedule or available. */
  const getTimelineBlocks = useCallback(
    (feUserId: number): { startHour: number; endHour: number; schedule: FESchedule | null }[] => {
      const list = (feAvailabilityByUser[feUserId] ?? []).filter((s) =>
        s.schedule_date?.startsWith(recommendDateStr)
      )
      const hourToSchedule: (FESchedule | null)[] = Array(24).fill(null)
      for (const s of list) {
        const startM = timeToMinutes(s.schedule_time)
        const endM = s.schedule_end_time ? timeToMinutes(s.schedule_end_time) : startM + 60
        const startHour = Math.max(0, Math.floor(startM / 60))
        // End hour inclusive: 09:00–12:00 spans columns 9,10,11,12 (4 hrs)
        const endHour = Math.min(24, Math.floor(endM / 60) + 1)
        for (let h = startHour; h < endHour; h++) hourToSchedule[h] = s
      }
      const blocks: { startHour: number; endHour: number; schedule: FESchedule | null }[] = []
      let i = 0
      while (i < 24) {
        const s = hourToSchedule[i]
        let end = i + 1
        while (end < 24 && hourToSchedule[end] === s) end++
        blocks.push({ startHour: i, endHour: end, schedule: s })
        i = end
      }
      return blocks
    },
    [feAvailabilityByUser, recommendDateStr, timeToMinutes]
  )

  /** Add one hour to "HH:mm" (e.g. "09:00" -> "10:00", "23:00" -> "00:00" wraps) */
  const addOneHour = useCallback((timeStr: string): string => {
    const m = timeToMinutes(timeStr) + 60
    const h = Math.floor(m / 60) % 24
    const min = m % 60
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
  }, [timeToMinutes])

  const openAssignModal = useCallback(
    (rec: FieldEngineerRecommendation, dateStr: string, timeStr = '09:00') => {
      setAssignModalRec(rec)
      setAssignModalDateStr(dateStr)
      setAssignModalTime(timeStr)
      setAssignModalEndTime(addOneHour(timeStr))
      setAssignModalRole('primary')
      setEditingScheduleId(null)
      onAssignModalOpen()
    },
    [onAssignModalOpen, addOneHour]
  )

  const existingSchedulesForModal = useMemo(() => {
    if (!assignModalRec || !assignModalDateStr) return []
    const feUserId = getFeUserId(assignModalRec)
    const list = feAvailabilityByUser[feUserId] ?? []
    return list.filter((s) => s.schedule_date?.startsWith(assignModalDateStr))
  }, [assignModalRec, assignModalDateStr, feAvailabilityByUser])

  const handleEditSchedule = useCallback((s: FESchedule) => {
    setEditingScheduleId(s.id)
    const start = s.schedule_time ?? '09:00'
    setEditScheduleTime(start)
    setEditScheduleEndTime(s.schedule_end_time ?? addOneHour(start))
    setEditScheduleRole(s.role)
  }, [addOneHour])

  const handleSaveEditSchedule = useCallback(async () => {
    if (!ticketId || editingScheduleId == null) return
    if (timeToMinutes(editScheduleEndTime) <= timeToMinutes(editScheduleTime)) {
      showError(new Error('End time must be after start time.'))
      return
    }
    setEditSubmitting(true)
    try {
      const res = await TicketService.updateFESchedule(ticketId, editingScheduleId, {
        schedule_time: editScheduleTime,
        schedule_end_time: editScheduleEndTime,
        role: editScheduleRole,
      })
      if (res.success) {
        showSuccess('Schedule updated')
        setEditingScheduleId(null)
        fetchFEAvailabilityForDate(recommendDateStr)
        refreshSchedulesInView()
      } else {
        const msg = res.message ?? 'Update failed'
        showError(
          new Error(
            /conflict|overlap|already has an assignment/i.test(msg)
              ? 'Schedule conflict detected. The Field Engineer already has an assignment during this time.'
              : msg
          )
        )
      }
    } catch (err) {
      showError(err as Error)
    } finally {
      setEditSubmitting(false)
    }
  }, [ticketId, editingScheduleId, editScheduleTime, editScheduleEndTime, editScheduleRole, timeToMinutes, recommendDateStr, fetchFEAvailabilityForDate, refreshSchedulesInView, showSuccess, showError])

  const handleDeleteScheduleClick = useCallback((scheduleId: number) => {
    setScheduleToDelete(scheduleId)
    onDeleteScheduleOpen()
  }, [onDeleteScheduleOpen])

  const handleDeleteScheduleConfirm = useCallback(async () => {
    if (!ticketId || scheduleToDelete == null) return
    const scheduleId = scheduleToDelete
    setScheduleToDelete(null)
    onDeleteScheduleClose()
    setDeleteSubmittingId(scheduleId)
    try {
      const res = await TicketService.deleteFESchedule(ticketId, scheduleId)
      if (res.success) {
        showSuccess('Schedule removed')
        if (editingScheduleId === scheduleId) setEditingScheduleId(null)
        fetchFEAvailabilityForDate(recommendDateStr)
        refreshSchedulesInView()
      } else {
        showError(new Error(res.message ?? 'Delete failed'))
      }
    } catch (err) {
      showError(err as Error)
    } finally {
      setDeleteSubmittingId(null)
    }
  }, [ticketId, scheduleToDelete, editingScheduleId, recommendDateStr, fetchFEAvailabilityForDate, refreshSchedulesInView, onDeleteScheduleClose, showSuccess, showError])

  const handleAssignSubmit = useCallback(async () => {
    if (!ticketId || !assignModalRec) return
    const feUserId = getFeUserId(assignModalRec)
    setAssignSubmitting(true)
    try {
      if (timeToMinutes(assignModalEndTime) <= timeToMinutes(assignModalTime)) {
        showError(new Error('End time must be after start time.'))
        setAssignSubmitting(false)
        return
      }
      const res = await TicketService.createFESchedule(ticketId, {
        fe_user_id: feUserId,
        schedule_date: assignModalDateStr,
        schedule_time: assignModalTime,
        schedule_end_time: assignModalEndTime,
        role: assignModalRole,
      })
      if (res.success) {
        showSuccess('FE scheduled')
        onAssignModalClose()
        setAssignModalRec(null)
        fetchFEAvailabilityForDate(recommendDateStr)
        refreshSchedulesInView()
      } else {
        const msg = res.message ?? ''
        if (/conflict|overlap|already has an assignment/i.test(msg)) {
          showError(new Error('Schedule conflict detected. The Field Engineer already has an assignment during this time.'))
        } else {
          showError(new Error(res.message ?? 'Assign failed'))
        }
      }
    } catch (err) {
      showError(err as Error)
    } finally {
      setAssignSubmitting(false)
    }
  }, [ticketId, assignModalRec, assignModalDateStr, assignModalTime, assignModalEndTime, assignModalRole, timeToMinutes, recommendDateStr, fetchFEAvailabilityForDate, refreshSchedulesInView, onAssignModalClose, showSuccess, showError])

  if (!request && !ticket) {
    return null
  }

  const serviceLabel = request
    ? [request.serviceParent, request.serviceType].filter(Boolean).join(' → ')
    : undefined

  const assets = ticket?.assets ?? []
  const showAssets = assets.length > 0

  return (
      <Box
        minW={0}
        {...(embedded
          ? {}
          : {
              bg: colors.cardBg,
              borderRadius: 'lg',
              border: '1px solid',
              borderColor: colors.border,
              overflow: 'hidden',
            })}
      >
        {/* ─── Header ─── */}
        {!embedded && (
          <Box px={4} py={3} borderBottom="1px solid" borderColor={colors.borderLight}>
            <Text fontWeight="700" color={colors.primary}>
              Ticket Details
            </Text>
          </Box>
        )}

        <Box px={embedded ? 0 : 4} py={embedded ? 0 : 4}>
          <VStack align="stretch" spacing={mainVSpacing}>

            {/* ════════════════════════════════════════════
                SECTION 1: Request Details
                (from ticket_requests table)
               ════════════════════════════════════════════ */}
            {request && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <SectionHeader>Request Details</SectionHeader>
                  {request.id && (
                    <Badge fontSize="xs" bg="purple.50" color="purple.700" border="1px solid" borderColor="purple.200" borderRadius="md" px={2.5} py={0.5} fontWeight="700">
                      {request.id}
                    </Badge>
                  )}
                </HStack>

                <VStack align="stretch" spacing={sectionVSpacing}>
                  {/* Contact Information */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Contact Name" value={request.contactName} />
                    <Pair label="Contact Email" value={request.contactEmail} />
                    <Pair label="Contact Number" value={request.contactNumber} />
                  </SimpleGrid>

                  {/* Service & Classification */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Service Type" value={serviceLabel || undefined} />
                    <Pair label="Request Source" value={request.requestSource} />
                    <Box>
                      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>
                        Categories
                      </Text>
                      {request.categories && request.categories.length > 0 ? (
                        <HStack spacing={1} flexWrap="wrap">
                          {request.categories.map((cat) => (
                            <Badge
                              key={cat}
                              fontSize="xs"
                              colorScheme="blue"
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              {cat}
                            </Badge>
                          ))}
                        </HStack>
                      ) : (
                        <Text fontSize="sm" color={colors.text} fontWeight="500">—</Text>
                      )}
                    </Box>
                  </SimpleGrid>

                  {/* Schedule & Timeline */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Requested Quantity" value={request.requestedQuantity?.toString()} />
                    <Pair label="Preferred Schedule" value={formatDate(request.preferredScheduleAt)} />
                    <Pair label="Preferred Contact At" value={formatDate(request.preferredContactAt)} />
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Submitted" value={formatDate(request.submittedAt)} />
                    <Pair label="Acknowledged" value={formatDate(request.acknowledgedAt)} />
                    <Pair label="Requested At" value={formatDate(request.requestedAt)} />
                  </SimpleGrid>

                  {/* Request Description (only if different from ticket description) */}
                  {request.description && request.description !== ticket?.description && (
                    <Box>
                      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={1}>
                        Request Description
                      </Text>
                      <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7">
                        {request.description}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}

            {/* ════════════════════════════════════════════
                SECTION 2: Parent Ticket Details
                (from parent_tickets table)
               ════════════════════════════════════════════ */}
            {ticket && (
              <>
                {request && <Divider borderColor={colors.borderLight} borderWidth="2px" />}
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <SectionHeader>Ticket Information</SectionHeader>
                    {ticket.ticketNumber && (
                      <Badge fontSize="xs" bg="blue.50" color="blue.700" border="1px solid" borderColor="blue.200" borderRadius="md" px={2.5} py={0.5} fontWeight="700">
                        {ticket.ticketNumber}
                      </Badge>
                    )}
                  </HStack>
                  {/* Row 1: Severity, Support Type, Source */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                    <Pair label="Severity" value={ticket.severity != null ? (SEVERITY_LABELS[String(ticket.severity)] ?? ticket.severity ?? ticket.priority) : (ticket.priority ?? undefined)} />
                    <Pair label="Support Type" value={ticket.supportType ? (SUPPORT_TYPE_LABELS[ticket.supportType] ?? ticket.supportType) : undefined} />
                    <Pair label="Source" value={ticket.source} />
                  </SimpleGrid>
                  {/* Row 2: Description */}
                  <Box mt={4}>
                    <SectionHeader>Description</SectionHeader>
                    <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7" mt={2}>
                      {ticket.description || '—'}
                    </Text>
                  </Box>
                  {/* Rows 3-5: Store Details */}
                  {storeDetails && (
                    <Box mt={5} pt={2}>
                      <SectionHeader mb={4}>Store Details</SectionHeader>
                      <VStack align="stretch" spacing={sectionVSpacing}>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                          <Pair label="Business Unit Name" value={storeDetails.businessUnitName} />
                          <Pair label="Store Name" value={storeDetails.storeName} />
                          <Pair label="Branch Code" value={storeDetails.branchCode} />
                        </SimpleGrid>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={gridX} spacingY={gridY}>
                          <Pair label="Contact Person" value={storeDetails.contactPerson} />
                          <Box>
                            <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>Email</Text>
                            {storeDetails.email ? (
                              <Link href={`mailto:${storeDetails.email}`} color={colors.primary} fontWeight="600" _hover={{ textDecoration: 'underline' }} fontSize="sm">
                                {storeDetails.email}
                              </Link>
                            ) : (
                              <Text fontSize="sm" color={colors.textMuted}>—</Text>
                            )}
                          </Box>
                          <Pair label="Contact Number" value={storeDetails.contactNumber} />
                        </SimpleGrid>
                        <Box>
                          <Pair label="Store Address" value={storeDetails.storeAddress} />
                        </Box>
                      </VStack>
                    </Box>
                  )}
                  {/* Row 6: Work Permit */}
                  <Box mt={4}>
                    <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={1}>
                      Work Permit
                    </Text>
                    <HStack spacing={3} flexWrap="wrap" align="flex-start">
                      {ticketId && onWorkPermitUpload && (
                        <Button
                          size="sm"
                          variant="solid"
                          colorScheme="blue"
                          as="label"
                          cursor="pointer"
                          sx={{ '& input': { display: 'none' } }}
                        >
                          Upload
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                onWorkPermitUpload(ticketId, file).finally(() => { e.target.value = '' })
                              }
                            }}
                          />
                        </Button>
                      )}
                      {ticketId && ticket.workPermitPath ? (
                        <WorkPermitThumbnail ticketId={ticketId as string} path={ticket.workPermitPath as string} />
                      ) : (
                        <Text fontSize="sm" color={colors.textSecondary}>No file attached</Text>
                      )}
                    </HStack>
                  </Box>
                </Box>
              </>
            )}

            {/* Parts information (between Description and Notes) */}
            {ticketId && (
              <>
                <Divider borderColor={colors.borderLight} />
                <PartsInfoSection ticketId={ticketId} isReadOnly={isReadOnly ?? true} onPartsChange={onPartsChange} />
              </>
            )}

            {/* FE Schedule (inside Ticket Information) – simple FE list like Assigned Team */}
            {ticketId && (
              <>
                <Divider borderColor={colors.borderLight} />
                <Box>
                  <HStack justify="space-between" align="center" mb={2}>
                    <SectionHeader>FE Schedule</SectionHeader>
                    {storeId != null && (
                      <Button size="sm" colorScheme="blue" variant="outline" onClick={handleRecommendFE} isLoading={recommendLoading}>
                        Recommend FE
                      </Button>
                    )}
                  </HStack>
                  {feScheduleRefreshing ? (
                    <HStack py={6} justify="center" gap={3}>
                      <Spinner size="sm" color={colors.primary} />
                      <Text fontSize="sm" color={colors.textSecondary}>Loading FE schedule...</Text>
                    </HStack>
                  ) : (
                    <>
                      {ticket?.scheduledAt && (
                        <Text fontSize="sm" color={colors.textSecondary} mb={2}>
                          Primary engineer schedule: {formatDateUTC(ticket.scheduledAt)}
                        </Text>
                      )}
                      {(primaryEngineer || backupEngineer) ? (
                    <VStack align="stretch" spacing={3}>
                      {primaryEngineer && (
                        <Box>
                          <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={1}>
                            Primary Engineer
                          </Text>
                          <Box
                            p={3}
                            borderRadius="md"
                            border="1px solid"
                            borderColor={colors.borderLight}
                            bg={colors.bg}
                          >
                            <Text fontSize="sm" fontWeight="600" color={colors.text}>
                              {primaryEngineer.full_name ?? '—'}
                            </Text>
                            {primaryEngineer.user_email && (
                              <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>{primaryEngineer.user_email}</Text>
                            )}
                            {(primaryEngineer.cluster || primaryEngineer.launch_point) && (
                              <Text fontSize="xs" color={colors.textMuted} mt={0.5}>
                                {primaryEngineer.cluster ?? ''}{primaryEngineer.cluster && primaryEngineer.launch_point ? ' · ' : ''}{primaryEngineer.launch_point ?? ''}
                              </Text>
                            )}
                          </Box>
                        </Box>
                      )}
                      {backupEngineer && (
                        <Box>
                          <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={1}>
                            Backup Engineer
                          </Text>
                          <Box
                            p={3}
                            borderRadius="md"
                            border="1px solid"
                            borderColor={colors.borderLight}
                            bg={colors.bg}
                          >
                            <Text fontSize="sm" fontWeight="600" color={colors.text}>
                              {backupEngineer.full_name ?? '—'}
                            </Text>
                            {backupEngineer.user_email && (
                              <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>{backupEngineer.user_email}</Text>
                            )}
                            {(backupEngineer.cluster || backupEngineer.launch_point) && (
                              <Text fontSize="xs" color={colors.textMuted} mt={0.5}>
                                {backupEngineer.cluster ?? ''}{backupEngineer.cluster && backupEngineer.launch_point ? ' · ' : ''}{backupEngineer.launch_point ?? ''}
                              </Text>
                            )}
                          </Box>
                        </Box>
                      )}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color={colors.textSecondary}>No FE assignments yet. Use Recommend FE to assign.</Text>
                  )}
                    </>
                  )}
                </Box>
              </>
            )}

            {/* Notes (parent_tickets.notes) — always show when ticket exists; editable via modal when ticketId + onNotesUpdate */}
            {ticket != null && (
              <>
                <Divider borderColor={colors.borderLight} />
                <Box role="group" position="relative">
                  <HStack justify="space-between" align="flex-start" gap={2}>
                    <SectionHeader>Notes</SectionHeader>
                    {ticketId && onNotesUpdate && (
                      <IconButton
                        aria-label="Edit notes"
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        icon={<Pencil size={14} />}
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        onClick={() => {
                          setNotesDraft(ticket?.notes ?? '')
                          onNotesModalOpen()
                        }}
                      />
                    )}
                  </HStack>
                  <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7" mt={2}>
                    {ticket?.notes?.trim() ? ticket.notes : 'No notes'}
                  </Text>
                </Box>
                {/* Edit Notes modal */}
                <Modal isOpen={isNotesModalOpen} onClose={onNotesModalClose} size="lg">
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>Edit notes</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <Textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder="Add or edit notes..."
                        minH="120px"
                        resize="vertical"
                      />
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="ghost" mr={3} onClick={onNotesModalClose} isDisabled={notesSaving}>
                        Cancel
                      </Button>
                      <Button
                        colorScheme="blue"
                        onClick={async () => {
                          if (!ticketId) return
                          setNotesSaving(true)
                          const res = await TicketService.updateParentTicketNotes(ticketId, notesDraft.trim() || null)
                          setNotesSaving(false)
                          if (res.success) {
                            await onNotesUpdate?.()
                            onNotesModalClose()
                            showSuccess(res.message ?? 'Notes updated')
                          } else {
                            showError(res.message ?? 'Failed to update notes')
                          }
                        }}
                        isLoading={notesSaving}
                      >
                        Save
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              </>
            )}

            {/* Assets (parent_tickets.assets) - only when Ticket Type = Preventive Maintenance */}
            {showAssets && (
              <>
                <Divider borderColor={colors.borderLight} />
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <SectionHeader>Assets</SectionHeader>
                    <Badge fontSize="xs" colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
                      {assets.length}
                    </Badge>
                  </HStack>
                  <TableContainer
                    maxH="260px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor={colors.borderLight}
                    borderRadius="md"
                  >
                    <Table size="sm" variant="simple" className="table">
                      <Thead bg={colors.bg} position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th fontSize="xs" py={2}>Tag #</Th>
                          <Th fontSize="xs" py={2}>Brand</Th>
                          <Th fontSize="xs" py={2}>Category</Th>
                          <Th fontSize="xs" py={2}>Model</Th>
                          <Th fontSize="xs" py={2}>Serial #</Th>
                          <Th fontSize="xs" py={2}>Function</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {assets.map((a) => (
                          <Tr key={a.id}>
                            <Td fontSize="xs" py={1.5}>{a.tag_number || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.brand_name || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.category_name || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.model || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.serial_number || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.function || '—'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </VStack>
        </Box>

        {/* Recommend FE Modal - Daily timeline (FE rows, hour columns) */}
        <Modal isOpen={isRecommendOpen} onClose={onRecommendClose} size="6xl">
          <ModalOverlay />
          <ModalContent maxW="90vw">
            <ModalHeader pr={12}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <Text>Recommended Field Engineers</Text>
                <HStack flexShrink={0} spacing={2} align="center">
                  <IconButton
                    aria-label="Previous day"
                    size="sm"
                    variant="outline"
                    icon={<ChevronLeft size={16} />}
                    onClick={() =>
                      setRecommendSelectedDate((d) => {
                        const next = new Date(d)
                        next.setDate(next.getDate() - 1)
                        return next
                      })
                    }
                  />
                  <Input
                    type="date"
                    size="sm"
                    value={recommendDateStr}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v) setRecommendSelectedDate(new Date(v))
                    }}
                    maxW="160px"
                  />
                  <IconButton
                    aria-label="Next day"
                    size="sm"
                    variant="outline"
                    icon={<ChevronRight size={16} />}
                    onClick={() =>
                      setRecommendSelectedDate((d) => {
                        const next = new Date(d)
                        next.setDate(next.getDate() + 1)
                        return next
                      })
                    }
                  />
                  <Button
                    size="sm"
                    variant={isSelectedToday ? 'solid' : 'outline'}
                    colorScheme="blue"
                    onClick={() => setRecommendSelectedDate(new Date())}
                  >
                    Today
                  </Button>
                </HStack>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              {recommendLoading ? (
                <HStack justify="center" py={8}>
                  <Spinner size="lg" />
                </HStack>
              ) : recommendations.length === 0 ? (
                <Text color={colors.textSecondary}>No recommendations. Ensure the ticket has a store with location.</Text>
              ) : (
                <>
                  {recommendScheduleLoading ? (
                    <HStack py={12} justify="center" gap={3}>
                      <Spinner size="md" color={colors.primary} />
                      <Text fontSize="sm" color={colors.textSecondary}>Loading FE schedule...</Text>
                    </HStack>
                  ) : (
                    <TableContainer overflowX="auto" whiteSpace="nowrap">
                      <Table size="sm" className="table">
                        <Thead>
                          <Tr>
                            <Th
                              className="sticky-col-left"
                              minW="220px"
                              borderRight="1px solid"
                              borderColor={colors.borderLight}
                              bg={colors.bg}
                            >
                              FE Name / Info
                            </Th>
                            {timelineHours.map((hour) => (
                              <Th
                                key={hour}
                                textAlign="center"
                                px={1}
                                fontSize="xs"
                                minW="60px"
                                border="1px solid"
                                borderColor={colors.borderLight}
                              >
                                {String(hour).padStart(2, '0')}:00
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {recommendations.map((rec, idx) => {
                            const feUserId = getFeUserId(rec)
                            return (
                              <Tr key={rec.field_engineer_id ?? idx}>
                                <Td
                                  className="sticky-col-left"
                                  borderRight="1px solid"
                                  borderColor={colors.borderLight}
                                  py={2}
                                  minW="220px"
                                  bg={colors.cardBg}
                                >
                                  <VStack align="stretch" spacing={0.5}>
                                    <Text fontWeight="600" fontSize="sm">
                                      {rec.full_name}
                                    </Text>
                                    <Text fontSize="xs" color={colors.textSecondary}>
                                      Score: {rec.score ?? '—'}
                                      {rec.distance_km != null ? ` · ${rec.distance_km} km` : ''}
                                    </Text>
                                    <Text fontSize="xs" color={colors.textSecondary}>
                                      Workload: {rec.workload ?? '—'} · Cluster: {rec.cluster ?? '—'}
                                    </Text>
                                    {rec.launch_point && (
                                      <Text fontSize="xs" color={colors.textMuted}>
                                        Launch: {rec.launch_point}
                                      </Text>
                                    )}
                                    {rec.has_required_certifications && (
                                      <Badge size="sm" colorScheme="green" fontSize="xs" w="fit-content">
                                        ✓ Certs
                                      </Badge>
                                    )}
                                    {rec.match_reasons && rec.match_reasons.length > 0 && (
                                      <HStack flexWrap="wrap" spacing={1} mt={0.5}>
                                        {rec.match_reasons.slice(0, 3).map((r) => (
                                          <Badge key={r} size="sm" variant="subtle" colorScheme="blue" fontSize="2xs">
                                            {r}
                                          </Badge>
                                        ))}
                                      </HStack>
                                    )}
                                  </VStack>
                                </Td>
                                {getTimelineBlocks(feUserId).map((block, blockIdx) => {
                                  const span = block.endHour - block.startHour
                                  const timeStr = `${String(block.startHour).padStart(2, '0')}:00`
                                  const isAvailable = block.schedule === null
                                  const canEditSchedule = block.schedule?.is_current_ticket === true
                                  const handleCellClick = () => {
                                    if (isAvailable) {
                                      openAssignModal(rec, recommendDateStr, timeStr)
                                    } else if (canEditSchedule && block.schedule) {
                                      setAssignModalRec(rec)
                                      setAssignModalDateStr(recommendDateStr)
                                      handleEditSchedule(block.schedule)
                                      onAssignModalOpen()
                                    }
                                  }
                                  return (
                                    <Td
                                      key={`${block.startHour}-${block.endHour}-${blockIdx}`}
                                      colSpan={span}
                                      px={1}
                                      py={1}
                                      minW={span * 60}
                                      cursor={isAvailable || canEditSchedule ? 'pointer' : 'default'}
                                      onClick={handleCellClick}
                                      _hover={isAvailable || canEditSchedule ? { bg: colors.borderLight } : undefined}
                                      border="1px solid"
                                      borderColor={colors.borderLight}
                                      bg={block.schedule ? 'orange.50' : undefined}
                                      verticalAlign="top"
                                    >
                                      {isAvailable ? (
                                        <Text fontSize="xs" color={colors.textSecondary}>
                                          Available
                                        </Text>
                                      ) : block.schedule ? (
                                        <Box fontSize="xs">
                                          <Text fontWeight="600">
                                            {block.schedule.schedule_time ?? '—'}
                                            {block.schedule.schedule_end_time ? `–${block.schedule.schedule_end_time}` : ''}
                                            {block.schedule.role === 'primary' ? ' (P)' : ' (S)'}
                                          </Text>
                                          <Text noOfLines={2} title={block.schedule.ticket_subject ?? undefined}>
                                            {block.schedule.ticket_subject || '—'}
                                          </Text>
                                          {!block.schedule.is_current_ticket ? (
                                            <Text fontSize="2xs" color={colors.textMuted}>
                                              Other ticket
                                            </Text>
                                          ) : (
                                            <Text fontSize="2xs" color={colors.textMuted}>
                                              Click to edit
                                            </Text>
                                          )}
                                        </Box>
                                      ) : null}
                                    </Td>
                                  )
                                })}
                              </Tr>
                            )
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Assign FE – time selection modal */}
        <Modal isOpen={isAssignModalOpen} onClose={() => { onAssignModalClose(); setAssignModalRec(null); setEditingScheduleId(null) }} size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Assign FE – {assignModalDateStr || 'Select date'}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>Ticket Information – Subject</Text>
                  <Text fontSize="sm" color={colors.text}>{ticket?.subject ?? '—'}</Text>
                </Box>

                {existingSchedulesForModal.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="600" mb={2}>Scheduled times for this date</Text>
                    <VStack align="stretch" spacing={2}>
                      {existingSchedulesForModal.map((s) => {
                        const canEdit = s.is_current_ticket
                        return (
                          <Box
                            key={s.id}
                            p={2}
                            border="1px solid"
                            borderColor={colors.borderLight}
                            borderRadius="md"
                            bg={colors.bg}
                            opacity={canEdit ? 1 : 0.85}
                          >
                            {editingScheduleId === s.id && canEdit ? (
                              <VStack align="stretch" spacing={2}>
                                <HStack align="flex-end" spacing={2}>
                                  <Box flex={1}>
                                    <Text fontSize="xs" fontWeight="600" mb={1}>Start</Text>
                                    <Input
                                      type="time"
                                      size="sm"
                                      value={editScheduleTime}
                                      onChange={(e) => setEditScheduleTime(e.target.value)}
                                      w="full"
                                    />
                                  </Box>
                                  <Box flex={1}>
                                    <Text fontSize="xs" fontWeight="600" mb={1}>End</Text>
                                    <Input
                                      type="time"
                                      size="sm"
                                      value={editScheduleEndTime}
                                      onChange={(e) => setEditScheduleEndTime(e.target.value)}
                                      w="full"
                                    />
                                  </Box>
                                </HStack>
                                <HStack>
                                  <Text fontSize="xs" fontWeight="600" minW="40px">Role</Text>
                                  <Select
                                    size="sm"
                                    value={editScheduleRole}
                                    onChange={(e) => setEditScheduleRole(e.target.value as 'primary' | 'secondary')}
                                  >
                                    <option value="primary">Primary FE</option>
                                    <option value="secondary">Secondary FE</option>
                                  </Select>
                                </HStack>
                              </VStack>
                            ) : (
                              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                                <Box>
                                  <Text fontWeight="600" fontSize="sm">
                                    {s.schedule_time ?? '—'}
                                    {s.schedule_end_time ? ` – ${s.schedule_end_time}` : ''}
                                  </Text>
                                  <Text fontSize="xs" color={colors.textSecondary}>
                                    {s.role === 'primary' ? 'Primary FE' : 'Secondary FE'}
                                    {!canEdit && ' · Other ticket (read-only)'}
                                  </Text>
                                </Box>
                                {canEdit && (
                                  <HStack>
                                    <Button size="xs" variant="outline" onClick={() => handleEditSchedule(s)}>
                                      Edit
                                    </Button>
                                    <Button
                                      size="xs"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => handleDeleteScheduleClick(s.id)}
                                      isLoading={deleteSubmittingId === s.id}
                                      isDisabled={deleteSubmittingId != null}
                                    >
                                      Delete
                                    </Button>
                                  </HStack>
                                )}
                              </HStack>
                            )}
                          </Box>
                        )
                      })}
                    </VStack>
                  </Box>
                )}

                {editingScheduleId == null && (
                  <Box pt={existingSchedulesForModal.length > 0 ? 2 : 0} borderTop={existingSchedulesForModal.length > 0 ? '1px solid' : undefined} borderColor={colors.borderLight}>
                    <Text fontSize="sm" fontWeight="600" mb={2}>{existingSchedulesForModal.length > 0 ? 'Add another time' : 'Time range'}</Text>
                    <VStack align="stretch" spacing={3}>
                      <HStack align="flex-end" spacing={3}>
                        <Box flex={1}>
                          <Text fontSize="xs" color={colors.textSecondary} mb={1}>Start</Text>
                          <Input
                            type="time"
                            value={assignModalTime}
                            onChange={(e) => setAssignModalTime(e.target.value)}
                          />
                        </Box>
                        <Box flex={1}>
                          <Text fontSize="xs" color={colors.textSecondary} mb={1}>End</Text>
                          <Input
                            type="time"
                            value={assignModalEndTime}
                            onChange={(e) => setAssignModalEndTime(e.target.value)}
                          />
                        </Box>
                      </HStack>
                      <Box>
                        <Text fontSize="xs" color={colors.textSecondary} mb={1}>Role</Text>
                        <Select
                          value={assignModalRole}
                          onChange={(e) => setAssignModalRole(e.target.value as 'primary' | 'secondary')}
                        >
                          <option value="primary">Primary FE</option>
                          <option value="secondary">Secondary FE</option>
                        </Select>
                      </Box>
                    </VStack>
                  </Box>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={2} onClick={() => { onAssignModalClose(); setAssignModalRec(null); setEditingScheduleId(null) }}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={editingScheduleId != null ? handleSaveEditSchedule : handleAssignSubmit}
                isLoading={editingScheduleId != null ? editSubmitting : assignSubmitting}
              >
                {editingScheduleId != null ? 'Save' : 'Assign'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <AlertDialog isOpen={isDeleteScheduleOpen} leastDestructiveRef={cancelDeleteRef} onClose={onDeleteScheduleClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>Remove scheduled time?</AlertDialogHeader>
              <AlertDialogBody>
                This will remove the Field Engineer from this time slot for the current ticket.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelDeleteRef} onClick={onDeleteScheduleClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={handleDeleteScheduleConfirm} ml={3}>
                  Remove
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    )
}
